import { useState, useEffect, useRef } from "react";
import { getInstafeedsApiUrl } from "../config/globalConfig";

export default function InstagramEmbed() {
  const [instafeeds, setInstafeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const componentRef = useRef(null);

  useEffect(() => {
    fetchInstafeeds();
  }, []);

  const fetchInstafeeds = async () => {
    try {
      setLoading(true);
      const response = await fetch(getInstafeedsApiUrl('GET_ALL'));
      const data = await response.json();
      
      if (data.result && data.Instafeeds) {
        setInstafeeds(data.Instafeeds);
      } else {
        setError('No Instagram feeds found');
      }
    } catch (err) {
      console.error('Error fetching Instagram feeds:', err);
      setError('Failed to load Instagram feeds');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (instafeeds.length > 0 && !isInitialized) {
      // Load Instagram embed script
      const loadInstagramScript = () => {
        return new Promise((resolve) => {
          // Check if script already exists
          let existingScript = document.querySelector('script[src="https://www.instagram.com/embed.js"]');
          
          if (existingScript) {
            resolve();
            return;
          }

          const script = document.createElement("script");
          script.src = "https://www.instagram.com/embed.js";
          script.async = true;
          script.id = "instagram-embed-script";
          script.onload = () => resolve();
          script.onerror = () => resolve(); // Continue even if script fails
          document.body.appendChild(script);
        });
      };

      const initializeEmbeds = async () => {
        await loadInstagramScript();
        
        // Wait for Instagram to be available
        const waitForInstagram = () => {
          return new Promise((resolve) => {
            const checkInstagram = () => {
              if (window.instgrm && window.instgrm.Embeds) {
                resolve();
              } else {
                setTimeout(checkInstagram, 100);
              }
            };
            checkInstagram();
          });
        };

        await waitForInstagram();
        
        // Process Instagram embeds
        if (window.instgrm && window.instgrm.Embeds) {
          window.instgrm.Embeds.process();
          setIsInitialized(true);
        }
      };

      initializeEmbeds();
    }
  }, [instafeeds, isInitialized]);

  // Re-initialize when component mounts (route change)
  useEffect(() => {
    if (instafeeds.length > 0 && isInitialized) {
      const reinitializeEmbeds = () => {
        setTimeout(() => {
          if (window.instgrm && window.instgrm.Embeds) {
            window.instgrm.Embeds.process();
          }
        }, 500);
      };

      reinitializeEmbeds();
    }
  }, [instafeeds, isInitialized]);

  if (loading) {
    return (
      <div className="feed-item">
        <div style={{ textAlign: 'center', padding: '20px' }}>
          Loading Instagram feeds...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="feed-item">
        <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
          {error}
        </div>
      </div>
    );
  }

  if (instafeeds.length === 0) {
    return (
      <div className="feed-item">
        <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
          No Instagram feeds available
        </div>
      </div>
    );
  }

  return (
    <div className="feeds-grid" ref={componentRef}>
      {instafeeds.slice(0, 4).map((feed, index) => (
        <div key={index} className="feed-item">
          <a 
            href={feed.instafeed_url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="instagram-post-link"
          >
            <blockquote
              className="instagram-media"
              data-instgrm-permalink={feed.instafeed_url}
              data-instgrm-version="14"
              data-instgrm-captioned="false"
              data-instgrm-embed-only="true"
            ></blockquote>
          </a>
        </div>
      ))}
    </div>
  );
}
